import { __assign, __read, __rest, __spread, __values } from "tslib";
import React, { forwardRef, useContext } from "react";
import warning from "warning";
import classNames from "classnames";
import { CheckTree, completeDisabledNames, } from "../../checktree";
import { Checkbox } from "../../checkbox";
import { getRowKeyFromRecordKey } from "../util/get-row-key-from-record-key";
import { CheckContext } from "../../check";
import { injectPropsIfTargetNotExisted } from "../util/inject-props-if-target-not-existed";
import { useConfig } from "../../_util/config-context";
var fallbackColumnKey = "__selectable_addon__";
var fallbackAllKey = "__selectable_all__";
export function selectable(options) {
    var e_1, _a;
    var _b = options.relations, relations = _b === void 0 ? {} : _b, value = options.value, onChange = options.onChange, _c = options.all, all = _c === void 0 ? true : _c, targetColumnKey = options.targetColumnKey, indent = options.indent, rowSelect = options.rowSelect, _d = options.width, width = _d === void 0 ? 26 : _d, _e = options.render, render = _e === void 0 ? function (x) { return x; } : _e, _f = options.shouldRecordExcludeFromAll, shouldRecordExcludeFromAll = _f === void 0 ? "disabled" : _f, _g = options.rowSelectable, rowSelectable = _g === void 0 ? function () { return true; } : _g;
    if (typeof indent !== "undefined") {
        warning(false, "Property `indent` is deprecated. Please use `indentable` addon instead.");
    }
    var allKey = typeof all === "string" ? all : fallbackAllKey;
    var rowDisabled = null;
    var childrenMap = new Map();
    try {
        for (var _h = __values(Object.entries(relations)), _j = _h.next(); !_j.done; _j = _h.next()) {
            var _k = __read(_j.value, 2), child = _k[0], parent_1 = _k[1];
            var childrenSet = childrenMap.get(parent_1);
            if (!childrenSet) {
                childrenSet = new Set();
                childrenMap.set(parent_1, childrenSet);
            }
            childrenSet.add(child);
        }
    }
    catch (e_1_1) { e_1 = { error: e_1_1 }; }
    finally {
        try {
            if (_j && !_j.done && (_a = _h.return)) _a.call(_h);
        }
        finally { if (e_1) throw e_1.error; }
    }
    var relationsKeys = Object.keys(relations);
    return {
        getInfo: function () { return ({ name: "selectable" }); },
        onInjectProps: function (props) {
            var columns = props.columns;
            rowDisabled = props.rowDisabled || (function () { return false; });
            columns = injectPropsIfTargetNotExisted(columns, targetColumnKey, {
                key: fallbackColumnKey,
                width: width,
                header: null,
                render: function () { return null; },
            });
            return __assign(__assign({}, props), { columns: columns });
        },
        onInjectColumn: function (previous) { return function (record, rowKey, recordIndex, column, columnIndex) {
            // 不是目标列
            if (column.key !== targetColumnKey && column.key !== fallbackColumnKey) {
                return previous(record, rowKey, recordIndex, column, columnIndex);
            }
            var _a = previous(record, rowKey, recordIndex, column, columnIndex), preChildren = _a.children, props = _a.props, result = __rest(_a, ["children", "props"]);
            var children = preChildren;
            // 表头
            if (recordIndex === -1) {
                children = (React.createElement(React.Fragment, null,
                    React.createElement(Checkbox, { style: all ? undefined : { display: "none" }, name: allKey }, children || React.createElement(React.Fragment, null, "\u00A0")),
                    !all ? children : null));
            }
            // 记录行
            else {
                var paddingLeft = 0;
                var depth = 0;
                if (indent > 0) {
                    var node = rowKey;
                    while (relations && relations[node]) {
                        depth += 1;
                        node = relations[node];
                    }
                    paddingLeft = indent * depth;
                }
                var checkbox = (React.createElement(TableCheckbox, { name: rowKey }, children || React.createElement(React.Fragment, null, "\u00A0")));
                var element = paddingLeft ? (React.createElement("div", { style: { paddingLeft: paddingLeft } }, checkbox)) : (checkbox);
                children = render(element, {
                    children: preChildren,
                    record: record,
                    depth: depth,
                    rowKey: rowKey,
                    recordIndex: recordIndex,
                    disabled: rowDisabled(record),
                });
            }
            return __assign(__assign({}, result), { props: props, children: children });
        }; },
        onInjectTable: function (renderBody) { return function (props) {
            var e_2, _a;
            var recordKey = props.recordKey, records = props.records, _b = props.rowDisabled, rowDisabled = _b === void 0 ? function () { return false; } : _b;
            var rowKey = getRowKeyFromRecordKey(recordKey);
            // 哪些应该从 all 里面排除
            var shouldRecordExcludeFromAllFinal = null;
            if (typeof shouldRecordExcludeFromAll === "function") {
                shouldRecordExcludeFromAllFinal = shouldRecordExcludeFromAll;
            }
            else if (shouldRecordExcludeFromAll === "disabled") {
                shouldRecordExcludeFromAllFinal = rowDisabled;
            }
            shouldRecordExcludeFromAllFinal =
                shouldRecordExcludeFromAllFinal || (function () { return false; });
            var recordRowKeys = [];
            var recordMap = new Map();
            records.forEach(function (record, index) {
                var key = rowKey(record, index);
                recordRowKeys.push(key);
                recordMap.set(key, record);
            });
            var disabledKeys = Array.from(__spread(relationsKeys, recordRowKeys)).filter(function (key) {
                return !rowSelectable(key, {
                    parent: relations[key],
                    children: childrenMap.has(relations[key])
                        ? __spread(childrenMap.get(relations[key])) : [],
                    record: recordMap.get(key),
                });
            });
            var disabledKeySet = new Set(disabledKeys);
            // all keys
            var keys = Array.from(new Set(__spread(relationsKeys, records
                .filter(function (x) { return !shouldRecordExcludeFromAllFinal(x); })
                .map(rowKey)
                .filter(function (x) {
                return shouldRecordExcludeFromAll === "disabled"
                    ? !disabledKeySet.has(x)
                    : true;
            }))));
            // keys without children
            var rootKeys = relations ? keys.filter(function (key) { return !relations[key]; }) : keys;
            var relationWithLeafs = __assign({}, (relations || {}));
            try {
                for (var rootKeys_1 = __values(rootKeys), rootKeys_1_1 = rootKeys_1.next(); !rootKeys_1_1.done; rootKeys_1_1 = rootKeys_1.next()) {
                    var key = rootKeys_1_1.value;
                    relationWithLeafs[key] = allKey;
                }
            }
            catch (e_2_1) { e_2 = { error: e_2_1 }; }
            finally {
                try {
                    if (rootKeys_1_1 && !rootKeys_1_1.done && (_a = rootKeys_1.return)) _a.call(rootKeys_1);
                }
                finally { if (e_2) throw e_2.error; }
            }
            // 只有全选 Check 可用时
            var hasRelations = !!Object.keys(relationWithLeafs).length;
            var disabledNames = __spread(records.filter(function (x) { return rowDisabled(x); }).map(rowKey), disabledKeys);
            var disabledSet = completeDisabledNames(disabledNames, childrenMap);
            return (React.createElement(CheckTree, { shiftSelect: "virtualized", relations: relationWithLeafs, value: value, onChange: hasRelations
                    ? function (value, context) {
                        var _a;
                        var newValue = value;
                        // 处理连选
                        if (context.shiftSelect) {
                            var _b = context.shiftSelect, startName_1 = _b.startName, endName_1 = _b.endName;
                            var section_1 = new Set(value);
                            var inSection_1;
                            records.forEach(function (record, index) {
                                var key = rowKey(record, index);
                                if (inSection_1 && !disabledSet.has(key)) {
                                    section_1.add(key);
                                }
                                if (key === startName_1 || key === endName_1) {
                                    inSection_1 = !inSection_1;
                                    if (inSection_1 && !disabledSet.has(key)) {
                                        section_1.add(key);
                                    }
                                }
                            });
                            newValue = __spread(section_1);
                        }
                        var newValueSet = new Set(newValue);
                        var name = context.check.name || ((_a = context.shiftSelect) === null || _a === void 0 ? void 0 : _a.endName);
                        onChange(newValue, __assign(__assign({}, context), { check: __assign({ all: context.check.name === allKey }, context.check), checked: context.check.value, recordKey: name, record: records.find(function (record, index) { return name === rowKey(record, index); }), selectedRecords: records.filter(function (record, index) {
                                return newValueSet.has(rowKey(record, index));
                            }) }));
                    }
                    : null, disabledNames: disabledNames }, renderBody(props)));
        }; },
        onInjectRow: function (renderRow) { return function (record, rowKey, recordIndex, columns) {
            var _a = renderRow(record, rowKey, recordIndex, columns), prepends = _a.prepends, appends = _a.appends, preRow = _a.row;
            var row = preRow;
            // 支持整行选择
            row = (React.createElement(SelectWrapper, { key: rowKey, name: rowKey, rowSelect: rowSelect && !rowDisabled(record) }, row));
            return { prepends: prepends, row: row, appends: appends };
        }; },
    };
}
var SelectWrapper = forwardRef(function SelectWrapper(_a, ref) {
    var name = _a.name, rowSelect = _a.rowSelect, children = _a.children, props = __rest(_a, ["name", "rowSelect", "children"]);
    var context = useContext(CheckContext);
    if (context) {
        var checkProps = context.inject({ type: "checkbox", name: name });
        var onChange_1 = checkProps.onChange, value_1 = checkProps.value;
        var rowSelectProps = {
            onClick: function (event) {
                // 事件合并
                if (typeof props.onClick === "function") {
                    props.onClick(event);
                }
                if (typeof children.props.onClick === "function") {
                    children.props.onClick(event);
                }
                return onChange_1(!value_1, {
                    event: event,
                    check: { type: "checkbox", name: name, value: !value_1, all: false },
                });
            },
        };
        return React.cloneElement(children, __assign(__assign(__assign({}, props), { ref: ref, className: classNames(props.className, children.props.className, {
                "is-selected": !!value_1,
            }) }), (rowSelect ? rowSelectProps : {})));
    }
    return children;
});
function TableCheckbox(_a) {
    var children = _a.children, props = __rest(_a, ["children"]);
    var classPrefix = useConfig().classPrefix;
    return (React.createElement(Checkbox, __assign({ className: classPrefix + "-form-check--table-select" }, props), children));
}
//# sourceMappingURL=selectable.js.map